import { appearanceFields, secondaryButtonFields } from "./_shared";
/**
 * Hero section — the page-opening visual block. Supports 5 selectable
 * **variants** (picked via a thumbnail tile control in the editor):
 *   - `classic`    — featured product / uploaded image / logo-only (the original).
 *   - `hero-card`  — inset rounded promo card: the image sits inside a 12px-margin
 *                    24px-radius card behind a cream wash, with DARK text. Its
 *                    `cardFill` setting switches the photo between that inset
 *                    frame and an edge-to-edge fill (see the field's note).
 *   - `carousel` — rotating `slide` blocks (autoplay + arrows + dots).
 *   - `video`    — muted, looping background video (URL) with a poster fallback.
 *   - `showcase` — headline + CTA beside a small product grid.
 *   - `collage`  — headline + CTA with 2–3 offset images.
 *
 * Most settings are gated by `showIf: { variant: … }` so the inspector only
 * shows controls relevant to the chosen variant. Carousel content lives in
 * per-slide blocks, so the shared headline/subhead/CTA hide for it.
 *
 * Marked `criticalForSeo` because the hero owns the page's `<h1>` (for the
 * non-carousel variants; the carousel renders its active slide headline).
 */
// Variants that carry their content PER SLIDE (blocks + autoplay) rather than
// in the section's shared headline/subhead/CTA fields.
const SLIDE_VARIANTS = ["carousel", "edition", "spotlight"];
// Variants that share the single headline/subhead/CTA + eyebrow/logo controls.
// The slide variants are excluded — they carry that content per-slide instead.
const TEXT_VARIANTS = [
    "classic",
    "billboard",
    "video",
    "collage",
    "mosaic",
    "hero-card",
    "runway",
];
// Variants rendered over a full-bleed image/video/grid, so they expose the
// darkening overlay used to keep the overlaid text readable.
const OVERLAY_VARIANTS = [
    "classic",
    "carousel",
    "edition",
    "video",
    "collage",
    "mosaic",
    "hero-card",
    "runway",
];
// Image-grid variants that share the 3 image fields + image-fit control.
const IMAGE_GRID_VARIANTS = ["collage", "mosaic"];
// Variants whose content column is rendered by BuyerHeroSection (the only
// renderer wired for the `.hero-content` vertical-position + pin-CTA hooks).
const CONTENT_COLUMN_VARIANTS = ["classic", "hero-card"];
// Variants driven by the classic media pipeline (featured product / uploaded
// image / logo-only) — they own `mediaSource` + `image` and are the only ones
// whose renderer draws a secondary CTA.
const MEDIA_SOURCE_VARIANTS = ["classic", "hero-card", "runway", "billboard"];
export const heroSchema = {
    type: "hero",
    name: { ar: "الواجهة الرئيسية", en: "Hero" },
    description: {
        ar: "القسم الافتتاحي اللي بيظهر فوق في الصفحة الرئيسية.",
        en: "The opening block at the top of the homepage.",
    },
    criticalForSeo: true,
    maxPerTemplate: 1,
    availableOnTemplates: ["home"],
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع الواجهة", en: "Hero type" },
            helpText: {
                ar: "اختر شكل الواجهة الرئيسية. كل نوع له إعداداته الخاصة.",
                en: "Pick the hero layout. Each type has its own settings.",
            },
            options: [
                { value: "classic", label: { ar: "كلاسيكي", en: "Classic" } },
                { value: "carousel", label: { ar: "شرائح متحركة", en: "Carousel" } },
                { value: "video", label: { ar: "فيديو", en: "Video" } },
                { value: "collage", label: { ar: "كولاج (٣ أعمدة)", en: "Collage" } },
                { value: "mosaic", label: { ar: "فسيفساء", en: "Mosaic" } },
                // Mobile-native rounded promo card (compact image card + pill CTA).
                { value: "hero-card", label: { ar: "بطاقة", en: "Card" } },
                // Ported from the EasyOrders `runway-hero` (5 of their 13 engines):
                // full-bleed image, gradient scrim, serif headline, pill CTAs.
                { value: "runway", label: { ar: "رنواي", en: "Runway" } },
                // Ported from the EasyOrders `edition-hero-slider` (dark, luxury): an
                // autoplaying crossfade slider with a progress bar and prev/pause/next.
                { value: "edition", label: { ar: "إديشن", en: "Edition" } },
                // Ported from the EasyOrders `ps-slider` (avyro, nature): a peek
                // carousel — one wide slide centred, neighbours showing at the edges.
                { value: "spotlight", label: { ar: "سبوت لايت", en: "Spotlight" } },
                // Ported from the EasyOrders `bottom-banner` (sharp): a full-bleed
                // photograph with the copy and CTA at opposite ends of one row.
                { value: "billboard", label: { ar: "بيلبورد", en: "Billboard" } },
            ],
            default: "classic",
        },
        {
            /**
             * How the card hero frames its photo. This is a FRAMING choice on the card,
             * not a variant of its own: the card hero is what `MOBILE_DEFAULTS` injects
             * on phones, so a second variant next to it could never be selected — the
             * hero has no variant picker (the media chips own `variant`) and the phone
             * would re-inject `hero-card` over any other pick. A plain setting sidesteps
             * both. Only meaningful on the card, hence the gate.
             */
            id: "cardFill",
            type: "select",
            label: { ar: "إطار الصورة", en: "Photo framing" },
            helpText: {
                ar: "الصورة جوه بطاقة بحواف مدورة، ولا مالية العرض كله من الحرف للحرف.",
                en: "Sit the photo inside the rounded card, or let it fill the section edge to edge.",
            },
            options: [
                { value: "inset", label: { ar: "جوه البطاقة", en: "Inside the card" } },
                { value: "full", label: { ar: "صورة كاملة", en: "Full photo" } },
            ],
            default: "inset",
            showIf: { variant: ["hero-card"] },
        },
        {
            id: "showLogo",
            type: "checkbox",
            label: { ar: "إظهار الشعار في الواجهة", en: "Show logo in hero" },
            helpText: {
                ar: "إظهار شعار المتجر فوق نص الواجهة.",
                en: "Show the store logo above the hero text.",
            },
            default: true,
            showIf: { variant: TEXT_VARIANTS },
        },
        {
            id: "showEyebrow",
            type: "checkbox",
            label: { ar: "إظهار النص الصغير فوق العنوان", en: "Show eyebrow label" },
            helpText: {
                ar: "النص الصغير اللي فوق العنوان الرئيسي (مثل: موسم جديد · أساسيات).",
                en: "The small label above the headline (e.g. NEW SEASON · ESSENTIALS).",
            },
            default: true,
            showIf: { variant: TEXT_VARIANTS },
        },
        {
            id: "eyebrow",
            type: "text",
            i18n: true,
            label: { ar: "نص صغير فوق العنوان", en: "Eyebrow label" },
            validate: { max: 40 },
            default: { ar: "موسم جديد · أساسيات", en: "NEW SEASON · ESSENTIALS" },
            showIf: { variant: TEXT_VARIANTS, showEyebrow: true },
        },
        {
            id: "headline",
            type: "text",
            i18n: true,
            label: { ar: "العنوان الرئيسي", en: "Headline" },
            helpText: {
                ar: "أكبر سطر في الواجهة. يفضّل بين 8 و80 حرف.",
                en: "The largest line in the hero. Aim for 8–80 characters.",
            },
            validate: { min: 8, max: 80 },
            showIf: { variant: TEXT_VARIANTS },
        },
        {
            id: "subhead",
            type: "textarea",
            i18n: true,
            label: { ar: "النص الفرعي", en: "Subheadline" },
            helpText: {
                ar: "سطر أو اتنين تحت العنوان الرئيسي.",
                en: "One or two short lines under the headline.",
            },
            validate: { max: 200 },
            showIf: { variant: TEXT_VARIANTS },
        },
        {
            id: "ctaLabel",
            type: "text",
            i18n: true,
            label: { ar: "نص زر الإجراء", en: "CTA label" },
            validate: { max: 24 },
            default: { ar: "تسوق الآن", en: "Shop now" },
            showIf: { variant: TEXT_VARIANTS },
        },
        {
            id: "ctaHref",
            type: "select",
            label: { ar: "وجهة الزر", en: "CTA destination" },
            options: [
                {
                    value: "all_products",
                    label: { ar: "كل المنتجات", en: "All products" },
                },
                {
                    value: "collections",
                    label: { ar: "تصفح الفئات", en: "Browse collections" },
                },
                {
                    value: "scroll_below",
                    label: { ar: "القسم التالي بالأسفل", en: "Scroll to next section" },
                },
            ],
            default: "all_products",
            showIf: { variant: TEXT_VARIANTS },
        },
        {
            // Destination for the secondary "Browse collections" button. Same choices
            // as the primary CTA; edited from the canvas Link menu (hidden in the
            // sidebar). Accepts a custom URL / "#section-…" anchor too.
            id: "ctaHrefSecondary",
            type: "select",
            label: { ar: "وجهة الزر الثاني", en: "Secondary button destination" },
            options: [
                {
                    // Added 2026-07-20 for the elite family. The secondary button had NO
                    // off switch: it renders whenever `ctaHrefSecondary` resolves and the
                    // field defaults to `collections`, so every media-pipeline hero shows
                    // two buttons and a seller who wants one cannot get one. Elite's
                    // `runway-hero` ships a single CTA, which is what surfaced it.
                    value: "none",
                    label: { ar: "بدون زر ثاني", en: "No second button" },
                },
                {
                    value: "all_products",
                    label: { ar: "كل المنتجات", en: "All products" },
                },
                {
                    value: "collections",
                    label: { ar: "تصفح الفئات", en: "Browse collections" },
                },
                {
                    value: "scroll_below",
                    label: { ar: "القسم التالي بالأسفل", en: "Scroll to next section" },
                },
            ],
            default: "collections",
            // A1: the secondary CTA only renders on the media-pipeline heroes.
            showIf: { variant: MEDIA_SOURCE_VARIANTS },
        },
        // --- Classic ---------------------------------------------------------
        {
            id: "mediaSource",
            type: "select",
            label: { ar: "مصدر الصورة", en: "Media source" },
            options: [
                {
                    value: "featured_product",
                    label: { ar: "منتج مميز", en: "Featured product" },
                },
                { value: "upload", label: { ar: "صورة مخصصة", en: "Upload image" } },
                {
                    value: "logo_only",
                    label: { ar: "اللوجو والنص فقط", en: "Logo and text only" },
                },
            ],
            default: "featured_product",
            // The card + full-photo variants reuse the classic media pipeline
            // (featured product / upload / logo-only) for their image surface.
            showIf: { variant: MEDIA_SOURCE_VARIANTS },
        },
        {
            id: "image",
            type: "image",
            label: { ar: "الصورة", en: "Image" },
            helpText: {
                ar: "الحجم المقترح: 1600×900 بكسل بحد أقصى 500 كيلوبايت.",
                en: "Recommended: 1600×900 px, under 500 KB.",
            },
            showIf: { variant: MEDIA_SOURCE_VARIANTS, mediaSource: "upload" },
            focalPoint: true,
            // `BuyerHeroSection` force-covers the hero image, so a fit toggle here is
            // a dead control — see `fitLocked` in types.ts.
            fitLocked: true,
        },
        // --- Carousel --------------------------------------------------------
        {
            id: "autoplayInterval",
            type: "range",
            label: { ar: "مدة عرض الشريحة (ثانية)", en: "Slide duration (seconds)" },
            helpText: {
                ar: "الوقت قبل الانتقال للشريحة التالية تلقائيًا.",
                en: "Time before auto-advancing to the next slide.",
            },
            min: 2,
            max: 10,
            step: 1,
            default: 5,
            showIf: { variant: SLIDE_VARIANTS },
        },
        {
            /**
             * `edition` is our renderer for TWO different EasyOrders slider engines,
             * which is why the control treatment has to be selectable rather than
             * baked in. MEASURED 2026-07-22 across all six families that use it:
             *
             *   `edition-hero-slider`  dark, luxury, volta   -> "rail"
             *   `ab-slider`            phoenix               -> "inline"
             *   `ab-slider` (arrows)   pearl, peral          -> "inline-arrows"
             *
             * "rail" is the default because it is what the variant was ported from
             * and what three of the six families still render byte-for-byte.
             *
             * pearl and peral share ONE upstream treatment, verified 2026-07-22 on
             * pearl-bags and peral-gallery: identical row, identical buttons. Note
             * that `pearl-watches` is NOT pearl's source — it ships a runway hero
             * and has no `ab-slider` at all.
             */
            id: "sliderControls",
            type: "select",
            label: { ar: "شكل أدوات التنقل", en: "Slider controls" },
            helpText: {
                ar: "شريط أسفل الصورة بأسهم، أو صف واحد بشريط تقدّم وزر إيقاف دائري.",
                en: "A bottom rail with arrows, or a single row with a progress bar and a round pause button.",
            },
            options: [
                { value: "rail", label: { ar: "شريط سفلي", en: "Bottom rail" } },
                { value: "inline", label: { ar: "صف واحد", en: "Inline row" } },
                {
                    value: "inline-arrows",
                    label: { ar: "صف واحد بأسهم", en: "Inline row with arrows" },
                },
            ],
            default: "rail",
            showIf: { variant: ["edition"] },
        },
        // --- Video -----------------------------------------------------------
        {
            id: "videoUrl",
            type: "url",
            label: { ar: "رابط الفيديو", en: "Video URL" },
            helpText: {
                ar: "رابط YouTube أو Vimeo أو ملف MP4 مباشر. يعمل تلقائيًا وبدون صوت.",
                en: "A YouTube, Vimeo, or direct MP4 link. Plays muted on loop.",
            },
            showIf: { variant: "video" },
        },
        {
            id: "videoPoster",
            type: "image",
            focalPoint: true,
            label: { ar: "صورة الغلاف", en: "Poster image" },
            helpText: {
                ar: "تظهر قبل تحميل الفيديو أو عند تعذّر التشغيل التلقائي.",
                en: "Shown before the video loads, or where autoplay is blocked.",
            },
            showIf: { variant: "video" },
        },
        // --- Collage / Mosaic (image grids) ---------------------------------
        {
            id: "collageImage1",
            type: "image",
            label: { ar: "الصورة الأولى", en: "Image 1" },
            showIf: { variant: IMAGE_GRID_VARIANTS },
            focalPoint: true,
        },
        {
            id: "collageImage2",
            type: "image",
            label: { ar: "الصورة الثانية", en: "Image 2" },
            showIf: { variant: IMAGE_GRID_VARIANTS },
            focalPoint: true,
        },
        {
            id: "collageImage3",
            type: "image",
            label: { ar: "الصورة الثالثة (اختياري)", en: "Image 3 (optional)" },
            showIf: { variant: IMAGE_GRID_VARIANTS },
            focalPoint: true,
        },
        {
            id: "imageFit",
            type: "select",
            label: { ar: "ملء الصور", en: "Image fit" },
            helpText: {
                ar: "احتواء: تظهر الصورة كاملة. تغطية: تملأ الإطار مع قص.",
                en: "Contain shows the whole image; cover fills the frame (cropped).",
            },
            options: [
                { value: "contain", label: { ar: "احتواء", en: "Contain" } },
                { value: "cover", label: { ar: "تغطية", en: "Cover" } },
            ],
            default: "contain",
            showIf: { variant: IMAGE_GRID_VARIANTS },
        },
        // --- Shared visual controls -----------------------------------------
        {
            id: "overlayOpacity",
            type: "range",
            label: { ar: "تعتيم الطبقة", en: "Overlay opacity" },
            // E2: inert whenever the hero resolves no background media
            // (`BuyerHeroSection` renders the overlay at `heroImageUrl ? alpha : 0`) —
            // i.e. `mediaSource: 'logo_only'`, an empty `upload`, or a featured
            // product with no image. That last case lives outside the section's
            // settings, and a `mediaSource` gate would misfire on the variants that
            // don't show `mediaSource` but keep a stale value, so this is a hint
            // rather than a `showIf`.
            helpText: {
                ar: "بيعتّم صورة الخلفية أو الفيديو عشان النص يبان أوضح. مالوش تأثير من غير صورة أو فيديو.",
                en: "Darkens the background photo or video so the text is readable. No effect until the hero has one.",
            },
            min: 0,
            max: 100,
            step: 5,
            default: 40,
            showIf: { variant: OVERLAY_VARIANTS },
        },
        {
            id: "textAlign",
            type: "select",
            label: { ar: "محاذاة النص", en: "Text alignment" },
            options: [
                { value: "start", label: { ar: "البداية", en: "Start" } },
                { value: "center", label: { ar: "الوسط", en: "Center" } },
            ],
            default: "start",
            showIf: { variant: TEXT_VARIANTS },
        },
        {
            id: "contentVerticalAlign",
            type: "select",
            // Dead on PHONES. It works by giving `.hero-content` a cross-axis
            // `align-items`, which can only move the stack when the hero is TALLER
            // than its content. Measured at 390px: the stack's natural height (333px)
            // already exceeds the mobile hero's box (min-height 304px − 64px padding
            // = 240px), so the hero grows to fit it and there is 0px to distribute —
            // the three options render identically. Desktop/tablet have 54px/215px of
            // slack and move as expected (verified 90→117→144 / 69→119→169).
            //
            // NB this is content-dependent rather than structural (`hero-stack` is
            // `flex: 0 1 auto`, not stretched — copy short enough to fit under ~240px
            // WOULD leave slack). Scoped by device anyway: at 390px a headline +
            // subhead + CTA always overflows that budget, so for every realistic
            // store it is inert. If the mobile hero ever gains a taller min-height,
            // drop this gate.
            appliesTo: ["desktop", "tablet"],
            label: { ar: "موضع المحتوى", en: "Content position" },
            helpText: {
                ar: "مكان المحتوى رأسيًا داخل الهيرو.",
                en: "Where the content sits vertically inside the hero.",
            },
            options: [
                { value: "top", label: { ar: "أعلى", en: "Top" } },
                { value: "center", label: { ar: "وسط", en: "Center" } },
                { value: "bottom", label: { ar: "أسفل", en: "Bottom" } },
            ],
            default: "top",
            showIf: { variant: CONTENT_COLUMN_VARIANTS },
        },
        {
            id: "pinButtonToBottom",
            type: "checkbox",
            label: { ar: "تثبيت الزر في الأسفل", en: "Pin button to bottom" },
            helpText: {
                ar: "يخلي زر الهيرو ثابت تحت بغض النظر عن موضع النص.",
                en: "Keeps the hero button at the bottom regardless of the text position.",
            },
            default: false,
            showIf: { variant: CONTENT_COLUMN_VARIANTS },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            helpText: {
                ar: "يستخدم لتحسين هيكل الصفحة لمحركات البحث. الافتراضي H1.",
                en: "Used for SEO heading hierarchy. Default H1.",
            },
            default: 1,
            showIf: { variant: TEXT_VARIANTS },
        },
        ...appearanceFields(),
        // A1: only the media-pipeline heroes render a secondary CTA, so its
        // style/hide controls are dead under carousel/video/collage/mosaic.
        ...secondaryButtonFields({ variant: MEDIA_SOURCE_VARIANTS }),
    ],
    blocks: [
        {
            type: "badge",
            name: { ar: "شارة", en: "Badge" },
            description: {
                ar: "علامة صغيرة فوق العنوان الرئيسي.",
                en: "A small label above the headline.",
            },
            maxPerSection: 3,
            showIf: { variant: TEXT_VARIANTS },
            settings: [
                {
                    id: "text",
                    type: "text",
                    i18n: true,
                    label: { ar: "النص", en: "Text" },
                    validate: { min: 2, max: 24 },
                },
                {
                    id: "tone",
                    type: "select",
                    label: { ar: "النبرة", en: "Tone" },
                    options: [
                        { value: "neutral", label: { ar: "محايد", en: "Neutral" } },
                        { value: "accent", label: { ar: "مميز", en: "Accent" } },
                        { value: "success", label: { ar: "إيجابي", en: "Success" } },
                    ],
                    default: "accent",
                },
            ],
        },
        {
            type: "slide",
            name: { ar: "شريحة", en: "Slide" },
            description: {
                ar: "شريحة واحدة في العرض المتحرك: صورة وعنوان ونص وزر.",
                en: "One carousel slide: image, headline, subhead, and CTA.",
            },
            maxPerSection: 6,
            showIf: { variant: SLIDE_VARIANTS },
            settings: [
                {
                    id: "image",
                    type: "image",
                    focalPoint: true,
                    label: { ar: "الصورة", en: "Image" },
                    helpText: {
                        ar: "الحجم المقترح: 1600×900 بكسل.",
                        en: "Recommended: 1600×900 px.",
                    },
                },
                {
                    id: "headline",
                    type: "text",
                    i18n: true,
                    label: { ar: "العنوان", en: "Headline" },
                    validate: { max: 80 },
                },
                {
                    id: "subhead",
                    type: "textarea",
                    i18n: true,
                    label: { ar: "النص الفرعي", en: "Subheadline" },
                    validate: { max: 200 },
                },
                {
                    id: "ctaLabel",
                    type: "text",
                    i18n: true,
                    label: { ar: "نص الزر", en: "CTA label" },
                    validate: { max: 24 },
                },
                {
                    id: "ctaHref",
                    type: "url",
                    label: { ar: "رابط الزر", en: "CTA link" },
                    helpText: {
                        ar: "الصفحة اللي بيفتحها الزر (مثل /collections/حاجة).",
                        en: "Where the button links (e.g. /collections/something).",
                    },
                },
            ],
        },
    ],
    presets: [
        {
            name: { ar: "كلاسيكي", en: "Classic" },
            description: {
                ar: "عنوان من البداية، طبقة شفافة 40%، صورة منتج مميز.",
                en: "Start-aligned text, 40% overlay, featured-product image.",
            },
            settings: {
                variant: "classic",
                textAlign: "start",
                overlayOpacity: 40,
                mediaSource: "featured_product",
            },
        },
        {
            name: { ar: "بسيط", en: "Minimal" },
            description: {
                ar: "لوجو ونص في المنتصف بدون صورة خلفية.",
                en: "Centered logo + text, no background image.",
            },
            settings: {
                variant: "classic",
                mediaSource: "logo_only",
                textAlign: "center",
                overlayOpacity: 0,
            },
        },
        {
            name: { ar: "صورة جريئة", en: "Bold image" },
            description: {
                ar: "صورة مرفوعة بطبقة 60% — مظهر جريء.",
                en: "Uploaded image with a 60% overlay for a bold look.",
            },
            settings: {
                variant: "classic",
                mediaSource: "upload",
                textAlign: "start",
                overlayOpacity: 60,
            },
        },
    ],
};
//# sourceMappingURL=hero.js.map